import { useEffect, useState } from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  TeamMemberGiftContextFields,
  emptyGiftContextValues,
  giftContextFromMember,
  giftContextToPayload,
  type GiftContextFieldValues,
} from "@/components/profile/TeamMemberGiftContextFields";
import { ChevronDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { emailSchema, fullNameSchema } from "@/lib/schemas";

export interface TeamMemberFormData {
  name: string;
  email: string;
  birthday: Date | undefined;
  startDate: Date | undefined;
  dietaryRequirements: string[];
  // Allergies are kept distinct from dietary preferences because they're
  // medical (anaphylaxis-grade) and conflating them with vegan/halal
  // creates a real safety risk.
  allergies: string[];
  // MS390 Bug #8: "explicitly none" markers. When the buyer ticks
  // "No allergies" / "No dietary requirements" we persist these
  // booleans so the /team "needs attention" banner can distinguish
  // unknown-empty (default) from confirmed-empty (don't flag).
  // Selecting any chip / typing custom text auto-flips these to
  // true on save — explicit data implies the buyer knows.
  allergiesConfirmed: boolean;
  dietaryConfirmed: boolean;
  department: string;
  officeLocation: string;
  hobbies: string[];
  favouriteFoods: string[];
  personalNotes: string;
  giftPreferences: GiftContextFieldValues["giftPreferences"];
}

interface AddTeamMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: TeamMemberFormData) => void;
  companyId?: string;
  member?: {
    id: string;
    name: string;
    email: string;
    // Nullable since the team_member_seeding migration: admin-pre-seeded
    // rows have these blank until the user fills them in. The form
    // resolves null to "no default date selected", user must pick one
    // before saving (the schema still requires startDate).
    birthday: string | null;
    start_date: string | null;
    dietary_requirements: string[];
    allergies?: string[];
    // MS390 Bug #8: optional so callers passing a pre-MS390 row
    // (e.g. CSV import preview) don't break the type contract.
    // Missing/undefined collapses to false in the form, which
    // matches the DB default for backfilled rows.
    allergies_confirmed?: boolean;
    dietary_confirmed?: boolean;
    department: string | null;
    office_location: string | null;
    is_active: boolean;
    hobbies?: string[] | null;
    favourite_foods?: string[] | null;
    personal_notes?: string | null;
    gift_preferences?: GiftContextFieldValues["giftPreferences"] | string[] | null;
  };
  onDeactivate?: (id: string) => void;
}

const DIETARY_OPTIONS = [
  "Gluten-free",
  "Vegan",
  "Vegetarian",
  "Nut-free",
  "Dairy-free",
  "Halal",
  "Kosher",
];

const PREDEFINED_DIETARY = new Set(DIETARY_OPTIONS);

// AU food labelling "Big 10", the allergens covered by mandatory
// Australian declaration rules, distilled to the names the average
// person uses (e.g. "Shellfish" rather than "Crustacea + mollusks").
// Covers ~95% of cases; "Other" handles the long tail (lupin, mustard,
// celery, latex, salicylates, etc.) without making the form
// overwhelming. Keep the order alphabetical so the user can scan.
const ALLERGY_OPTIONS = [
  "Dairy",
  "Eggs",
  "Fish",
  "Gluten",
  "Peanuts",
  "Sesame",
  "Shellfish",
  "Soy",
  "Sulphites",
  "Tree nuts",
];

const PREDEFINED_ALLERGIES = new Set(ALLERGY_OPTIONS);

const teamMemberSchema = z.object({
  name: fullNameSchema,
  email: emailSchema,
  birthday: z.date().optional(),
  startDate: z.date({ required_error: "Pick a start date." }),
  dietary: z.array(z.string()).default([]),
  otherChecked: z.boolean().default(false),
  otherText: z.string().default(""),
  // MS390 Bug #8: explicit "no dietary requirements" toggle. When
  // ticked, the buyer is confirming this member has none (rather
  // than the buyer simply not having filled it in). Ticking auto-
  // clears any selected dietary chips so the form can't be in a
  // contradictory state ("Vegan + No dietary" is meaningless).
  noDietary: z.boolean().default(false),
  // Allergies as a multi-select with an Other fallback, mirroring the
  // dietary field's pattern. We curate the common allergens (Big 10)
  // so the form is fast for the typical case, and offer "Other" for
  // anything that doesn't fit (lupin, mustard, latex, salicylates,
  // etc.). Stored on team_members.allergies as a TEXT[] so each
  // entry can be rendered as a discrete coral badge downstream.
  allergies: z.array(z.string()).default([]),
  allergyOtherChecked: z.boolean().default(false),
  allergyOtherText: z.string().default(""),
  // MS390 Bug #8: explicit "no allergies" toggle. Same semantics
  // as noDietary above. Crucial: a member legitimately having no
  // allergies is the common case (most people), not the exception.
  noAllergies: z.boolean().default(false),
  department: z.string().default(""),
  officeLocation: z.string().default(""),
});

type TeamMemberSchemaInput = z.input<typeof teamMemberSchema>;
type TeamMemberSchemaValues = z.output<typeof teamMemberSchema>;

const emptyDefaults: TeamMemberSchemaInput = {
  name: "",
  email: "",
  birthday: undefined,
  // startDate intentionally typed as required Date but rhf lets us start
  // with undefined as long as the validator catches submit attempts without
  // it. Zod infers Date for the field, so we cast via assertion only at the
  // default-level to keep the rest of the form strict.
  startDate: undefined as unknown as Date,
  dietary: [],
  otherChecked: false,
  otherText: "",
  noDietary: false,
  allergies: [],
  allergyOtherChecked: false,
  allergyOtherText: "",
  noAllergies: false,
  department: "",
  officeLocation: "",
};

function giftDefaultsFromMember(
  member: AddTeamMemberDialogProps["member"],
): GiftContextFieldValues {
  if (!member) return emptyGiftContextValues();
  return giftContextFromMember(member);
}

export function AddTeamMemberDialog({
  open,
  onOpenChange,
  onSave,
  companyId,
  member,
  onDeactivate,
}: AddTeamMemberDialogProps) {
  const [addresses, setAddresses] = useState<{ id: string; label: string; address: string }[]>([]);
  const [giftContextOpen, setGiftContextOpen] = useState(false);
  const [giftContext, setGiftContext] = useState<GiftContextFieldValues>(emptyGiftContextValues());

  // useForm<TInput, TContext, TOutput>: TContext is unused here so we leave
  // it as `undefined` rather than `any`. Using `any` triggers the
  // no-explicit-any lint rule and was blocking CI.
  const form = useForm<TeamMemberSchemaInput, undefined, TeamMemberSchemaValues>({
    resolver: zodResolver(teamMemberSchema),
    defaultValues: emptyDefaults,
  });

  useEffect(() => {
    if (companyId && open) {
      supabase
        .from("company_addresses")
        .select("id, label, address")
        .eq("company_id", companyId)
        .then(({ data }) => setAddresses(data || []));
    }
  }, [companyId, open]);

  useEffect(() => {
    if (!open) return;
    if (member) {
      const allDietary = member.dietary_requirements || [];
      const predefinedDiet = allDietary.filter((d) => PREDEFINED_DIETARY.has(d));
      const customDiet = allDietary.filter((d) => !PREDEFINED_DIETARY.has(d));

      const allAllergies = member.allergies ?? [];
      const predefinedAllergies = allAllergies.filter((a) => PREDEFINED_ALLERGIES.has(a));
      const customAllergies = allAllergies.filter((a) => !PREDEFINED_ALLERGIES.has(a));

      form.reset({
        name: member.name,
        email: member.email,
        // Pre-seeded rows can have null dates; the form treats undefined
        // as "user must pick a date" rather than crashing on new Date(null).
        // MS439: append T00:00:00 so the date-picker initialises to the
        // correct local day rather than UTC midnight (wrong in AEST).
        birthday: member.birthday ? new Date(member.birthday + "T00:00:00") : undefined,
        startDate: member.start_date ? new Date(member.start_date + "T00:00:00") : undefined,
        dietary: predefinedDiet,
        otherChecked: customDiet.length > 0,
        otherText: customDiet.join(", "),
        // MS390 Bug #8: hydrate the "explicitly none" toggles. Only
        // show as ticked when the buyer previously confirmed AND the
        // list is empty — a confirmed row with data is a contradiction
        // we shouldn't re-surface as "No dietary".
        noDietary: (member.dietary_confirmed ?? false) && allDietary.length === 0,
        allergies: predefinedAllergies,
        allergyOtherChecked: customAllergies.length > 0,
        allergyOtherText: customAllergies.join(", "),
        noAllergies: (member.allergies_confirmed ?? false) && allAllergies.length === 0,
        department: member.department || "",
        officeLocation: member.office_location || "",
      });
      setGiftContext(giftDefaultsFromMember(member));
    } else {
      form.reset(emptyDefaults);
      setGiftContext(emptyGiftContextValues());
      setGiftContextOpen(false);
    }
  }, [member, open, form]);

  // Generic toggle for any string-array multi-select. Used by both
  // dietary and allergies, the behaviour is identical (add if absent,
  // remove if present) so a single helper keeps the field renderers
  // tidy.
  const toggleListItem = (current: string[], item: string) => {
    return current.includes(item) ? current.filter((d) => d !== item) : [...current, item];
  };

  const onSubmit = (values: TeamMemberSchemaValues) => {
    // MS390 Bug #8: "No dietary requirements" wins — if the buyer
    // ticked it we force an empty array even if stray chip state
    // is still in the form. The opposite (chips selected) implicitly
    // marks the field as confirmed.
    const dietaryCustom = values.otherChecked
      ? values.otherText.split(",").map((s) => s.trim()).filter(Boolean)
      : [];
    const allDietary = values.noDietary
      ? []
      : [...new Set([...values.dietary, ...dietaryCustom])];

    const allergyCustom = values.allergyOtherChecked
      ? values.allergyOtherText.split(",").map((s) => s.trim()).filter(Boolean)
      : [];
    const allAllergies = values.noAllergies
      ? []
      : [...new Set([...values.allergies, ...allergyCustom])];

    // A non-empty list is itself an implicit confirmation — the
    // buyer wouldn't enter "Peanuts" without knowing. So
    // confirmed = explicit toggle OR data present. This is the
    // load-bearing logic that fixes the "needs attention" banner.
    const dietaryConfirmed = values.noDietary || allDietary.length > 0;
    const allergiesConfirmed = values.noAllergies || allAllergies.length > 0;
    const enrichment = giftContextToPayload(giftContext);

    onSave({
      name: values.name.trim(),
      email: values.email.trim(),
      birthday: values.birthday,
      startDate: values.startDate,
      dietaryRequirements: allDietary,
      allergies: allAllergies,
      dietaryConfirmed,
      allergiesConfirmed,
      department: values.department.trim(),
      officeLocation: values.officeLocation.trim(),
      hobbies: enrichment.hobbies,
      favouriteFoods: enrichment.favourite_foods,
      personalNotes: enrichment.personal_notes ?? "",
      giftPreferences: enrichment.gift_preferences ?? {},
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-xl max-w-lg max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-navy">
            {member ? "Edit team member" : "Add team member"}
          </DialogTitle>
          <DialogDescription className="text-charcoal/70">
            {member ? "Update this team member's details." : "Add a new member to your team."}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 mt-2">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-navy font-medium">Full name *</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Sarah Johnson" autoComplete="name" className="mt-1" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-navy font-medium">Email *</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="sarah@company.com" autoComplete="email" className="mt-1" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="birthday"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-navy font-medium">Birthday</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className={cn(
                          "w-full mt-1 justify-start text-left font-normal",
                          !field.value && "text-charcoal/40",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {field.value ? format(field.value, "d MMMM yyyy") : "Select birthday"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        initialFocus
                        className="p-3 pointer-events-auto"
                        captionLayout="dropdown"
                        fromYear={1950}
                        toYear={new Date().getFullYear()}
                      />
                    </PopoverContent>
                  </Popover>
                  <p className="mt-1.5 text-xs text-charcoal/50">
                    No birthday? We'll skip celebrations for this member.
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="startDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-navy font-medium">Start date *</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className={cn(
                          "w-full mt-1 justify-start text-left font-normal",
                          !field.value && "text-charcoal/40",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {field.value ? format(field.value, "d MMMM yyyy") : "Select start date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      {/* MS397: add year bounds so a typo can't produce a
                          work-anniversary celebration 975 years in the future. */}
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        initialFocus
                        className="p-3 pointer-events-auto"
                        captionLayout="dropdown"
                        fromYear={1990}
                        toYear={new Date().getFullYear() + 2}
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="dietary"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-navy font-medium">Dietary preferences</FormLabel>
                  <p className="text-xs text-charcoal/60 -mt-1">
                    Lifestyle preferences only. Allergies go below.
                  </p>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {DIETARY_OPTIONS.map((item) => (
                      <label key={item} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={field.value?.includes(item) ?? false}
                          onCheckedChange={() => field.onChange(toggleListItem(field.value ?? [], item))}
                        />
                        <span className="text-sm text-charcoal">{item}</span>
                      </label>
                    ))}
                    <FormField
                      control={form.control}
                      name="otherChecked"
                      render={({ field: otherField }) => (
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={otherField.value}
                            onCheckedChange={(v) => {
                              otherField.onChange(!!v);
                              if (!v) form.setValue("otherText", "");
                            }}
                          />
                          <span className="text-sm text-charcoal">Other</span>
                        </label>
                      )}
                    />
                  </div>
                  {form.watch("otherChecked") && (
                    <FormField
                      control={form.control}
                      name="otherText"
                      render={({ field: otherField }) => (
                        <Textarea
                          placeholder="e.g. Low sodium, Pescatarian"
                          className="mt-2 min-h-[60px]"
                          {...otherField}
                        />
                      )}
                    />
                  )}
                  {/* MS390 Bug #8: explicit "No dietary requirements"
                      toggle so the buyer can mark a member as
                      confirmed-none rather than the field staying
                      ambiguously empty. Ticking it clears any chip
                      selection (the contradictory "Vegan + None" state
                      is mechanically prevented). Sits below the chips
                      so the buyer sees the menu first, then the
                      "actually, this person has none" escape hatch. */}
                  <FormField
                    control={form.control}
                    name="noDietary"
                    render={({ field: noField }) => (
                      <label className="mt-3 flex items-center gap-2 cursor-pointer text-sm text-charcoal/80">
                        <Checkbox
                          checked={noField.value}
                          onCheckedChange={(v) => {
                            const next = !!v;
                            noField.onChange(next);
                            if (next) {
                              form.setValue("dietary", []);
                              form.setValue("otherChecked", false);
                              form.setValue("otherText", "");
                            }
                          }}
                        />
                        <span>No dietary requirements</span>
                      </label>
                    )}
                  />
                </FormItem>
              )}
            />

            {/* Allergies, multi-select mirroring the dietary pattern.
                Curated list is the AU "Big 10" common allergens; "Other"
                handles long-tail (lupin, mustard, latex, salicylates,
                etc.). Coral dot + (medical) label keep the visual
                distinction from dietary preferences so users don't
                conflate the two, a peanut allergy is not a preference. */}
            <FormField
              control={form.control}
              name="allergies"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-navy font-medium flex items-center gap-1.5">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-coral" />
                    Allergies <span className="text-xs font-normal text-coral">(medical)</span>
                  </FormLabel>
                  <p className="text-xs text-charcoal/60 -mt-1">
                    Anything that could cause a reaction. Surfaced on every order so the vendor sees it.
                  </p>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {ALLERGY_OPTIONS.map((item) => (
                      <label key={item} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={field.value?.includes(item) ?? false}
                          onCheckedChange={() =>
                            field.onChange(toggleListItem(field.value ?? [], item))
                          }
                          className="data-[state=checked]:bg-coral data-[state=checked]:border-coral"
                        />
                        <span className="text-sm text-charcoal">{item}</span>
                      </label>
                    ))}
                    <FormField
                      control={form.control}
                      name="allergyOtherChecked"
                      render={({ field: otherField }) => (
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={otherField.value}
                            onCheckedChange={(v) => {
                              otherField.onChange(!!v);
                              if (!v) form.setValue("allergyOtherText", "");
                            }}
                            className="data-[state=checked]:bg-coral data-[state=checked]:border-coral"
                          />
                          <span className="text-sm text-charcoal">Other</span>
                        </label>
                      )}
                    />
                  </div>
                  {form.watch("allergyOtherChecked") && (
                    <FormField
                      control={form.control}
                      name="allergyOtherText"
                      render={({ field: otherField }) => (
                        <Input
                          placeholder="e.g. Lupin, Mustard, Latex"
                          className="mt-2 border-coral/30 focus-visible:border-coral"
                          {...otherField}
                        />
                      )}
                    />
                  )}
                  {/* MS390 Bug #8: "No allergies" toggle. Mirrors the
                      "No dietary" pattern above. Critical for the
                      common case — most members legitimately have no
                      allergies, and pre-MS390 the empty array kept the
                      /team needs-attention banner pinned forever. */}
                  <FormField
                    control={form.control}
                    name="noAllergies"
                    render={({ field: noField }) => (
                      <label className="mt-3 flex items-center gap-2 cursor-pointer text-sm text-charcoal/80">
                        <Checkbox
                          checked={noField.value}
                          onCheckedChange={(v) => {
                            const next = !!v;
                            noField.onChange(next);
                            if (next) {
                              form.setValue("allergies", []);
                              form.setValue("allergyOtherChecked", false);
                              form.setValue("allergyOtherText", "");
                            }
                          }}
                          className="data-[state=checked]:bg-coral data-[state=checked]:border-coral"
                        />
                        <span>No allergies</span>
                      </label>
                    )}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="department"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-navy font-medium">Department</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Marketing" className="mt-1" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="officeLocation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-navy font-medium">Office location</FormLabel>
                  {addresses.length > 0 ? (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select office" />
                      </SelectTrigger>
                      <SelectContent>
                        {addresses.map((a) => (
                          <SelectItem key={a.id} value={`${a.label}: ${a.address}`}>
                            {a.label}: {a.address}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <FormControl>
                      <Input placeholder="e.g. Melbourne HQ" className="mt-1" {...field} />
                    </FormControl>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <Collapsible open={giftContextOpen} onOpenChange={setGiftContextOpen}>
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-center justify-between rounded-xl border-2 border-navy/10 bg-butter/20 px-4 py-3 text-left transition-colors hover:border-coral/40"
                >
                  <div>
                    <p className="text-sm font-semibold text-navy">Gift personality</p>
                    <p className="text-xs text-charcoal/60">
                      Optional — helps Lembra recommend gifts they'll love
                    </p>
                  </div>
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 text-charcoal/50 transition-transform",
                      giftContextOpen && "rotate-180",
                    )}
                  />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-3 rounded-xl border-2 border-navy/10 bg-white p-4">
                <TeamMemberGiftContextFields
                  values={giftContext}
                  onChange={setGiftContext}
                  firstName={form.watch("name")?.trim().split(/\s+/)[0] || "them"}
                />
              </CollapsibleContent>
            </Collapsible>

            <div className="flex items-center gap-3 mt-6">
              {member && onDeactivate && (
                <Button
                  type="button"
                  variant="outline"
                  className="border-coral text-coral hover:bg-coral hover:text-white mr-auto"
                  onClick={() => onDeactivate(member.id)}
                >
                  {member.is_active ? "Deactivate" : "Reactivate"}
                </Button>
              )}
              <div className="flex gap-3 ml-auto">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary">
                  {member ? "Save changes" : "Add member"}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
